import React, { useRef } from 'react';

import { cssMap } from '@atlaskit/css';
import FileIcon from '@atlaskit/icon/core/file';
import ImageIcon from '@atlaskit/icon/core/image';
import LinkIcon from '@atlaskit/icon/core/link';
import PageIcon from '@atlaskit/icon/core/page';
import PagesIcon from '@atlaskit/icon/core/pages';
import StoryIcon from '@atlaskit/icon/core/story';
import WhiteboardIcon from '@atlaskit/icon/core/whiteboard';
import { Anchor, Box, Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { Conversation } from '../../../types';

const styles = cssMap({
	scopeLink: {
		textDecoration: 'none',
		color: token('color.text.subtle'),
		'&:hover': {
			color: token('color.text.subtle'),
		},
	},
	/** Same as `scopeLink` but for links inside the dark tooltip surface. */
	scopeLinkTooltip: {
		textDecoration: 'none',
		color: token('color.text.inverse'),
		'&:hover': {
			color: token('color.text.inverse'),
			textDecoration: 'underline',
		},
	},
	scopeLinkIconImage: {
		width: '12px',
		height: '12px',
		borderRadius: token('radius.small'),
		objectFit: 'cover',
		flexShrink: '0',
		display: 'block',
	},
});

export type ScopeRowVariant = 'default' | 'tooltip';

type ConversationScope = NonNullable<Conversation['scopes']>[number];
type ScopeRowScope = Omit<ConversationScope, 'link'> & {
	link: ConversationScope['link'] | undefined;
};

function getScopeIconComponent(
	scope: ScopeRowScope,
	showLinkFallbackIcon: boolean,
): typeof PageIcon | null {
	switch (scope.type) {
		case 'image':
			return ImageIcon;
		case 'file':
			return FileIcon;
		case 'jira':
			return StoryIcon;
		case 'confluence':
			switch (scope.sub_type) {
				case 'space':
					return PagesIcon;
				case 'whiteboard':
					return WhiteboardIcon;
				case 'page':
				default:
					return PageIcon;
			}
		case 'link':
			return showLinkFallbackIcon ? LinkIcon : null;
		default:
			return null;
	}
}

export type ScopeRowProps = {
	scope: ScopeRowScope;
	variant: ScopeRowVariant;
	maxLines: number;
	/** Whether to render the scope title beside its icon. Defaults to true. */
	showName?: boolean;
	/** Whether links without a remote icon use the generic link icon. Defaults to false. */
	showLinkFallbackIcon?: boolean;
	/** Whether to render the icon when the scope is not linked. Defaults to false. */
	showIconWithoutLink?: boolean;
	onFilePreview?: (link: string, name: string) => void;
	onScopeLinkClick?: (variant: ScopeRowVariant) => void;
	onScopeLinkHover?: () => void;
};

/**
 * Renders a single scope as an icon + name wrapped in an anchor.
 * Used in both the first-scope footer and in the tooltip scope list.
 *
 * Shared between `ChatItem` (conversation-assistant-ui-components) and
 * `ChatListItem` (rovo-navigation).
 */
export const ScopeRow = ({
	scope,
	variant,
	maxLines,
	showName = true,
	showLinkFallbackIcon = false,
	showIconWithoutLink = false,
	onFilePreview,
	onScopeLinkClick,
	onScopeLinkHover,
}: ScopeRowProps): JSX.Element => {
	const textColor = variant === 'tooltip' ? 'color.text.inverse' : 'color.text.subtlest';

	const iconColor =
		variant === 'tooltip' ? token('color.icon.inverse') : token('color.icon.subtle');

	const isFile = scope.type === 'file' || scope.type === 'image';
	const canUseIconUrl = scope.type === 'link' || scope.type === 'jira';
	const ScopeIcon = getScopeIconComponent(scope, showLinkFallbackIcon);

	const hasRemoteScopeIcon =
		canUseIconUrl && typeof scope.icon_url === 'string' && scope.icon_url.trim().length > 0;

	// Prefer a remote `icon_url` (links / jira) when present; otherwise fall back to the built-in icon.
	const icon = hasRemoteScopeIcon ? (
		<Box
			as="img"
			src={scope.icon_url}
			alt={showName ? '' : scope.name}
			xcss={styles.scopeLinkIconImage}
		/>
	) : ScopeIcon ? (
		<ScopeIcon label={showName ? '' : scope.name} size="small" color={iconColor} />
	) : null;

	// Defensively resolve `link` to a string — the API may return an object or
	// null/undefined for freshly-pasted links before backend normalisation.
	const rawLink = scope.link as unknown;
	const resolvedLink =
		typeof rawLink === 'string'
			? rawLink
			: rawLink !== null && typeof rawLink === 'object'
				? String(
						(rawLink as Record<string, unknown>).url ??
							(rawLink as Record<string, unknown>).href ??
							'',
					)
				: '';

	// Guard: fire the hover analytics event only once per mouseenter/mouseleave
	// cycle, regardless of how many synthetic mouseenter events the browser emits.
	const hasHoveredRef = useRef(false);

	const handleMouseEnter = () => {
		if (!hasHoveredRef.current) {
			hasHoveredRef.current = true;
			onScopeLinkHover?.();
		}
	};

	const handleMouseLeave = () => {
		hasHoveredRef.current = false;
	};

	const handleClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		onScopeLinkClick?.(variant);
		if (isFile && onFilePreview) {
			e.preventDefault();
			onFilePreview(resolvedLink, scope.name);
		}
	};

	if (!resolvedLink) {
		return (
			<Inline alignBlock="center" space="space.050">
				{showIconWithoutLink && icon}
				{showName && (
					<Text size="small" weight="regular" maxLines={maxLines} align="start" color={textColor}>
						{scope.name}
					</Text>
				)}
			</Inline>
		);
	}

	return (
		<Inline alignBlock="center" space="space.050">
			<Anchor
				href={resolvedLink}
				aria-label={showName ? undefined : scope.name}
				{...(isFile ? {} : { target: '_blank', rel: 'noreferrer' })}
				xcss={variant === 'tooltip' ? styles.scopeLinkTooltip : styles.scopeLink}
				onClick={handleClick}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
			>
				<Inline alignBlock="center" space="space.050">
					{icon}
					{showName && (
						<Text size="small" weight="regular" maxLines={maxLines} align="start" color={textColor}>
							{scope.name}
						</Text>
					)}
				</Inline>
			</Anchor>
		</Inline>
	);
};
